import { Box, CircularProgress, Grid, ThemeProvider } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { CustomExplain } from '../components/CustomExplain'
import { CustomTitle } from '../components/CustomTitile'
import { Footer } from '../components/Footer'
import { IllustrationAddButton } from '../components/IllustrationAddButton'
import { IllustrationList } from '../components/IllustrationList'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { PicturesPaper } from '../components/PicturesPaper'
import { loadStatus } from '../constants/loadStatus'
import { mediaTheme } from '../constants/theme'
import { errorMsg } from '../constants/messages'
import { useAuth } from '../hooks/useAuth'
import { useC501IllustrationGet } from '../hooks/useC501IllustrationGet'
import { useC502IllustrationAdd } from '../hooks/useC502IllustrationAdd'
import { useC503IllustrationDelete } from '../hooks/useC503IllustrationDelete'
import { useC504IllustrationUpdate } from '../hooks/useC504IllustrationUpdate'
import { useC901SignedUrlGet } from '../hooks/useC901SignedUrlGet'
import { useCloudStorage } from '../hooks/useCloudStorage'
import { useAppUsagesIllustrationRegistered } from '../hooks/useAppUsagesIllustrationRegistered'
import type { IllustrationIf } from '../interfaces/IllustrationIf'
import { validateFileSize } from '../util/FileSize'
import { LanguageContext } from '../util/LanguageContext'
import { SideMenu } from './SideMenu'
import { useDeviceCapability } from '../util/DeviceCapabilityContext'

// 図解画面
export const Illustration = () => {
// hooks
  const auth = useAuth()
  const c501 = useC501IllustrationGet()
  const c502 = useC502IllustrationAdd()
  const c503 = useC503IllustrationDelete()
  const c504 = useC504IllustrationUpdate()
  const c901 = useC901SignedUrlGet()
  const cs = useCloudStorage()
  const appUsagesIllustrationRegistered = useAppUsagesIllustrationRegistered()
// 文字列変換用
  const translation = useContext(LanguageContext)
 // ローディングステータス監視
  useEffect(() => {
    if (
      c501.loading === loadStatus.LOADING ||
      c502.loading === loadStatus.LOADING ||
      c503.loading === loadStatus.LOADING ||
      c504.loading === loadStatus.LOADING ||
      c901.loading === loadStatus.LOADING ||
      cs.loading === loadStatus.LOADING ||
      appUsagesIllustrationRegistered.loading === loadStatus.LOADING
    ) {
      auth.setIsDisable(true)
    } else {
      auth.setIsDisable(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c501.loading, c502.loading, c503.loading, c504.loading, c901.loading, cs.loading, appUsagesIllustrationRegistered.loading])
// 図解配列
  const [illustrations, setIllustrations] = useState<IllustrationIf[]>([])
  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')

  const {supportsOverwrite} = useDeviceCapability()

  const pattern = /.(jpg|jpeg|png|mp4)$/i
  // 拡張子チェック関数
  const extensionCheck = async (file: string) => {
    const result = pattern.test(file)
    return result
  }

  // 図解追加ハンドラ
  const IllustrationAddHandler = async (illustration_name: string, File: File) => {
    // ファイルサイズチェック
    if (!validateFileSize(File)) {
      setErrorDialog(errorMsg.FILE_SIZE_ERROR)
      setOpenDialog(true)
      return
    }

    //拡張子チェック
    const result = await extensionCheck(File.name)

    if (result) {
      // 署名付きURL取得
      const resultUrl = await c901.execSignedUrlGet()
      if (resultUrl.success) {
        const resultCs = await cs.execUploadFile(resultUrl.message!, File)
        if (resultCs.success) {
          const result = await c502.execIllustrationAdd(
            auth.deviceId,
            illustration_name,
            File,
            resultUrl.message!,
            { overwrite: supportsOverwrite }
          )
          if (result.success) {
            // AppUsages 図解登録
            const usageResult = await appUsagesIllustrationRegistered.execIllustrationRegistered(auth.deviceId)
            if (!usageResult.success) {
              console.warn('AppUsages(ILLUSTRATION_REGISTERED) failed:', usageResult.message)
            }
            const result = await c501.execIllustrationGet(auth.deviceId)
            if (result.success) {
              if (result.info) {
                setIllustrations(result.info!)
              }
            } else {
              setErrorDialog(result.message!)
              setOpenDialog(true)
            }
          } else {
            setErrorDialog(result.message!)
            setOpenDialog(true)
          }
        } else {
          setErrorDialog(resultCs.message!)
          setOpenDialog(true)
        }
      } else {
        setErrorDialog(resultUrl.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(errorMsg.UNSUPPORTED_FILE_ERROR)
      setOpenDialog(true)
    }
  }
  // 図解削除ハンドラ
  const IllustrationDelHandler = async (inputValue: string) => {
    const result = await c503.execIllustrationDelete(auth.deviceId, inputValue)
    if (result.success) {
      const result = await c501.execIllustrationGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          setIllustrations(result.info!)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }

  // 図解単語編集ハンドラ
  const IllustrationEditHandler = async (oldText: string, newText: string) => {
    const result = await c504.execIllustrationUpdate(auth.deviceId, oldText, newText)
    if (result.success) {
      const r2 = await c501.execIllustrationGet(auth.deviceId)
      if (r2.success && r2.info) setIllustrations(r2.info!)
      else {
        setErrorDialog(r2.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }

  // 図解(図解ファイル追加)編集ハンドラ
  const IllustrationFileEditHandler = async (text: string, File: File) => {
     // ファイルサイズチェック
    if (!validateFileSize(File)) {
      setErrorDialog(errorMsg.FILE_SIZE_ERROR)
      setOpenDialog(true)
      return
    }
    //拡張子チェック
    const result = await extensionCheck(File.name)

    if (result) {
      // 署名付きURL取得
      const resultUrl = await c901.execSignedUrlGet()
      if (resultUrl.success) {
        const resultCs = await cs.execUploadFile(resultUrl.message!, File)
        if (resultCs.success) {
          const result = await c504.execIllustrationUpdate(
            auth.deviceId,
            text,
            text,
            File,
            undefined,
            resultUrl.message!,
          )
          if (result.success) {
            const result = await c501.execIllustrationGet(auth.deviceId)
            if (result.success) {
              if (result.info) {
                setIllustrations(result.info!)
              }
            } else {
              setErrorDialog(result.message!)
              setOpenDialog(true)
            }
          } else {
            setErrorDialog(result.message!)
            setOpenDialog(true)
          }
        } else {
          setErrorDialog(resultCs.message!)
          setOpenDialog(true)
        }
      } else {
        setErrorDialog(resultUrl.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(errorMsg.UNSUPPORTED_FILE_ERROR)
      setOpenDialog(true)
    }
  }

  // 全画面ファイル編集（旧名＝新名で送る）
  const IllustrationFullFileEditHandler = async (text: string, fullFile: File) => {
    // ファイルサイズチェック
    if (!validateFileSize(fullFile)) {
      setErrorDialog(errorMsg.FILE_SIZE_ERROR)
      setOpenDialog(true)
      return
    }
    
    //拡張子チェック
    const result = await extensionCheck(fullFile.name)

    if (result) {
      // 署名付きURL取得
      const resultUrl = await c901.execSignedUrlGet()
      if (resultUrl.success) {
        const resultCs = await cs.execUploadFile(resultUrl.message!, fullFile)
        if (resultCs.success) {
          const result = await c504.execIllustrationUpdate(
            auth.deviceId,
            text,
            text,
            undefined,
            fullFile,
            resultUrl.message!,
          )
          if (result.success) {
            const result = await c501.execIllustrationGet(auth.deviceId)
            if (result.success) {
              if (result.info) {
                setIllustrations(result.info!)
              }
            } else {
              setErrorDialog(result.message!)
              setOpenDialog(true)
            }
          } else {
            setErrorDialog(result.message!)
            setOpenDialog(true)
          }
        } else {
          setErrorDialog(resultCs.message!)
          setOpenDialog(true)
        }
      } else {
        setErrorDialog(resultUrl.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(errorMsg.UNSUPPORTED_FILE_ERROR)
      setOpenDialog(true)
    }
  }

  // 起動時
  useEffect(() => {
    const fetchData = async () => {
      const result = await c501.execIllustrationGet(auth.deviceId)
      if (result.success && result.info) {
        setIllustrations(result.info!)
      } else if (!result.success) {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ダイアログクローズ
  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
  }

  return (
    <ThemeProvider theme={mediaTheme}>
      {openDialog && (
        <ModalConfirmDialog open={openDialog} text={errorDialog} onClick={handleDialogClose} />
      )}
      <Box sx={{ display: 'flex', p: 0 }}>
        <Box sx={{ p: 0, width: '338px', flexShrink: 0 }}>
          <SideMenu />
        </Box>
        <Box sx={{ flexGrow: 1, overflowX: 'auto' }} display="flex" flexDirection="column">
          <Box marginTop={13}>
            <CustomTitle>{translation['label_diagram']}</CustomTitle>
          </Box>
          <Box>
            <CustomExplain>{translation['note_file_size_limit']}</CustomExplain>
          </Box>
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
            }}
          >
            {(c501.loading === loadStatus.LOADING ||
              c502.loading === loadStatus.LOADING ||
              c503.loading === loadStatus.LOADING ||
              c504.loading === loadStatus.LOADING ||
              c901.loading === loadStatus.LOADING ||
              cs.loading === loadStatus.LOADING) && <CircularProgress />}
          </Box>
          <Box sx={{ marginTop: '40px' }}>
            <PicturesPaper>
              <Grid container spacing={2}>
                <Grid item lg={6} xl={4}>
                  <IllustrationAddButton
                    title={translation['title_illustration_name']}
                    illustrations={illustrations}
                    overwrite={supportsOverwrite}
                    onAdd={IllustrationAddHandler}
                  >
                    {translation['button_add_new']}
                  </IllustrationAddButton>
                </Grid>
              </Grid>
              <Grid container spacing={2} mt={2}>
                <IllustrationList
                  items={illustrations}
                  onEditIllustrationFile={(text, File) =>
                    IllustrationFileEditHandler(text, File)
                  }
                  onEditIllustrationFullFile={(text, fullFile) =>
                    IllustrationFullFileEditHandler(text, fullFile)
                  }
                  onEditIllustration={(oldText, newText) =>
                    IllustrationEditHandler(oldText, newText)
                  }
                  onDeleteIllustration={IllustrationDelHandler}
                />
              </Grid>
            </PicturesPaper>
          </Box>
        </Box>
      </Box>
      <Footer />
    </ThemeProvider>
  )
}